import React from 'react'
import {
  Box,
  Divider,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
} from '@chakra-ui/react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { useParams } from 'react-router-dom'
import { useArticles } from '../context/ArticlesContext'

export const FaqsArticle = () => {
  const { articleSlug } = useParams()
  const { allArticles, categories } = useArticles()

  const { isLoading } = categories || {}

  const article =
    allArticles && allArticles.find(art => art.slug === articleSlug)

  const processedMarkdown = article?.content
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/(.*?)\n-{3,}\n/g, (match, title) => {
      return `## ${title.trim()}\n`
    })
    .replace(/-{4,}/g, '<hr />')

  return (
    <Stack mx='auto' w='full' maxW={980} pb={12} pr={10} pt={6}>
      <Skeleton
        isLoaded={!isLoading}
        width={isLoading ? '450px' : '100%'}
        height={isLoading ? '43.19px' : 'auto'}
        alignSelf={'flex-start'}
      >
        <Heading fontSize='4xl' color={'#ffffff99'} fontWeight={700}>
          {article?.title}
        </Heading>
      </Skeleton>
      <SkeletonText
        isLoaded={!isLoading}
        noOfLines={2}
        spacing='2'
        skeletonHeight={'19px'}
        lineHeight={'28.5px'}
        w={'full'}
        py={4}
      >
        <Text
          fontFamily={'Inter'}
          fontSize={'19px'}
          lineHeight={'28.5px'}
          fontWeight={500}
          color={'#ffffff99'}
        >
          {article?.description}
        </Text>
      </SkeletonText>
      <Divider my={10} />
      <Box pt={6}>
        <SkeletonText
          isLoaded={!isLoading}
          noOfLines={12}
          spacing='4'
          skeletonHeight='20px'
          mb={6}
        >
          <ReactMarkdown
            unwrapDisallowed={true}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw as any]}
            components={{
              iframe: ({ node, ...props }) => (
                <iframe
                  {...props}
                  title='embedded-content'
                  style={{ marginBottom: '24px' }}
                />
              ),
              a: ({ node, ...props }) => (
                <Link
                  {...props}
                  color='blue.400'
                  target='_blank'
                  referrerPolicy='no-referrer'
                  mb={4}
                />
              ),
              ul: ({ node, ...props }) => (
                <List styleType='disc' lineHeight={'28.5px'} {...props} />
              ),
              ol: ({ node, ...props }) => (
                <List styleType='decimal' spacing={4} mb={6} {...props} />
              ),
              li: ({ node, ...props }) => <ListItem ml={5} {...props} />,
              strong: ({ node, ...props }) => (
                <Text as='strong' fontWeight='bold' mb={4} {...props} />
              ),
              em: ({ node, ...props }) => (
                <Text as='em' fontStyle='italic' mb={4} {...props} />
              ),
              h2: ({ node, ...props }) => (
                <Heading as='h2' fontSize={24} mt={8} mb={6} {...props} />
              ),
              hr: ({ node, ...props }) => (
                <hr style={{ marginBottom: '24px' }} {...props} />
              ),
              p: ({ node, ...props }) => (
                <Text my={'10px'} lineHeight={'28.5px'} {...props} />
              ),
              code: ({ node, inline, ...props }) => {
                if (inline) {
                  return (
                    <Box
                      as='code'
                      display='inline'
                      px={1}
                      bg='#1f212422'
                      color='violet'
                      borderRadius='sm'
                      fontFamily='monospace'
                      {...props}
                    />
                  )
                }
                return (
                  <Stack w='fit-content' mb={6}>
                    <Box
                      as='code'
                      p={2}
                      textAlign='center'
                      bg='#1f2124'
                      color='violet'
                      borderRadius='md'
                      {...props}
                    />
                  </Stack>
                )
              },
              img: ({ node, ...props }) => (
                <Image
                  {...props}
                  my={6}
                  borderRadius='md'
                  alt={'Post image'}
                  objectFit='contain'
                />
              ),
            }}
          >
            {processedMarkdown || ''}
          </ReactMarkdown>
        </SkeletonText>
      </Box>
    </Stack>
  )
}
